import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navLinks, teamItems, resourceItems, alternativeItems } from '../data/mock';
import BusyBeeLogo from './BusyBeeLogo';
import { BOT_GUIDES, ROLE_GUIDES, APP_GUIDES } from '../data/guides';
import LanguageSwitcher from './LanguageSwitcher';
import {
  ChevronDown, ArrowRight, Focus, Calendar, Clock, Users,
  Link as LinkIcon, Coffee, LayoutDashboard, Box, TrendingUp, Code,
  Heart, Megaphone, DollarSign, Briefcase, FileText, HelpCircle, Video,
  Book, Timer, Menu, X, Sparkles, Webhook, Mail, Plug, Bot,
  Inbox, Sliders, Repeat, Search, Pencil, Table2, Network, Trash2, Globe, Bell,
  Database, Smartphone, Languages, ArrowRightLeft,
  History, Map, Lightbulb, MessageSquare, ListChecks, Monitor, Brain, CalendarClock, ShieldCheck, Cpu,
  Rocket,
} from 'lucide-react';

const ICON_MAP = {
  focus: Focus,
  habits: Clock,
  tasks: LayoutDashboard,
  link: LinkIcon,
  users: Users,
  calendar: Calendar,
  coffee: Coffee,
  clock: Clock,
  layout: LayoutDashboard,
  box: Box,
  'trending-up': TrendingUp,
  code: Code,
  heart: Heart,
  megaphone: Megaphone,
  'dollar-sign': DollarSign,
  briefcase: Briefcase,
  'file-text': FileText,
  'help-circle': HelpCircle,
  video: Video,
  book: Book,
  timer: Timer,
  sparkles: Sparkles,
  webhook: Webhook,
  mail: Mail,
  plug: Plug,
  bot: Bot,
  inbox: Inbox,
  sliders: Sliders,
  repeat: Repeat,
  search: Search,
  pencil: Pencil,
  'table-2': Table2,
  network: Network,
  trash: Trash2,
  globe: Globe,
  bell: Bell,
  database: Database,
  smartphone: Smartphone,
  languages: Languages,
  'arrow-right-left': ArrowRightLeft,
  history: History,
  map: Map,
  lightbulb: Lightbulb,
  'message-square': MessageSquare,
  'list-checks': ListChecks,
  monitor: Monitor,
  brain: Brain,
  'calendar-clock': CalendarClock,
  'shield-check': ShieldCheck,
  cpu: Cpu,
  rocket: Rocket,
};

const COLOR_BG = {
  blue:   'bg-blue-500/10 text-blue-400',
  yellow: 'bg-amber-500/10 text-amber-400',
  pink:   'bg-pink-500/10 text-pink-400',
  green:  'bg-green-500/10 text-green-400',
  purple: 'bg-purple-500/10 text-purple-400',
  cyan:   'bg-cyan-500/10 text-cyan-400',
};

// Render a single megamenu item. `namespace` selects which subtree of
// `nav.items` / `nav.team_items` / `nav.resource_items` we resolve from.
// Items keep to one line of description, so a column of five fits a laptop screen.
const FeatureItem = ({ item, namespace, onNavigate }) => {
  const { t } = useTranslation('landing');
  const Icon = ICON_MAP[item.icon] || Sparkles;
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className="group flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
    >
      <div className={`p-1.5 rounded-md shrink-0 mt-0.5 ${COLOR_BG[item.color] || COLOR_BG.yellow}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-white text-sm font-medium group-hover:text-amber-400 transition-colors truncate">
          {t(`nav.${namespace}.${item.key}.name`)}
        </div>
        <div className="text-gray-500 text-xs leading-snug truncate">
          {t(`nav.${namespace}.${item.key}.desc`)}
        </div>
      </div>
    </Link>
  );
};

// A bot, use-case or integration page; its name and line come from guide_links.
const GuideItem = ({ guide, onNavigate }) => {
  const { t } = useTranslation('landing');
  const Icon = ICON_MAP[guide.icon] || Bot;
  const base = `guide_links.${guide.path.slice(1)}`;
  return (
    <Link to={guide.path} onClick={onNavigate} className="group flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors">
      <div className="p-1.5 rounded-md shrink-0 mt-0.5 bg-amber-500/10 text-amber-400">
        {guide.logo
          ? <img src={guide.logo} alt="" className="w-3.5 h-3.5 object-contain" />
          : <Icon className="w-3.5 h-3.5" />}
      </div>
      <div className="min-w-0">
        <div className="text-white text-sm font-medium group-hover:text-amber-400 transition-colors truncate">{t(`${base}.name`)}</div>
        <div className="text-gray-500 text-xs leading-snug truncate">{t(`${base}.desc`)}</div>
      </div>
    </Link>
  );
};

// A plain link with a small icon, for long lists (comparisons, teams, extras).
const PlainLink = ({ to, icon, children, onNavigate }) => {
  const Icon = ICON_MAP[icon] || ArrowRight;
  return (
    <Link to={to} onClick={onNavigate} className="group flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
      <Icon className="w-3.5 h-3.5 text-gray-500 group-hover:text-amber-400 transition-colors shrink-0" />
      <span className="truncate">{children}</span>
    </Link>
  );
};

const SectionTitle = ({ children, className = '' }) => (
  <div className={`text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-3 ${className}`}>{children}</div>
);

const MoreLink = ({ to, onNavigate, children }) => (
  <Link to={to} onClick={onNavigate} className="mt-3 px-3 text-sm text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center gap-1">
    {children}
    <ArrowRight className="w-3.5 h-3.5" />
  </Link>
);

const Navbar = () => {
  const { t } = useTranslation('landing');
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();
  const closeTimer = useRef(null);

  const showAnnouncement = !location.pathname.startsWith('/app');

  // Close menus on route change
  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const openMenu = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMenu(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActiveMenu(null), 120);
  };
  const onNavigate = () => {
    setActiveMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  };

  return (
    <>
      {/* Announcement banner */}
      {showAnnouncement && (
        <div className="bg-gradient-to-r from-amber-900/50 via-amber-800/50 to-amber-900/50 border-b border-amber-700/30">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm">
            <span className="text-amber-400">🐝</span>
            <span className="text-gray-300 hidden sm:inline">{t('announcement.long')}</span>
            <span className="text-gray-300 sm:hidden">{t('announcement.short')}</span>
            <Link
              to="/public/team/7L106DYwv0eDJxkOlQ9GRzup9W8mfMBd#changelog"
              className="text-amber-400 hover:text-amber-300 ml-2 flex items-center gap-1 font-medium whitespace-nowrap"
            >
              {t('announcement.learn_more')}
            </Link>
          </div>
        </div>
      )}

      {/* Main navbar */}
      {/* BG_60: the navbar is `position: sticky` and stays pinned for the
          entire page scroll. `backdrop-filter` forces the browser to re-sample
          and re-blur the backdrop on every scroll frame, a well-documented
          cause of scroll jank that scales with the blur radius and the
          machine's GPU. The background is already 95% opaque, so a 24px blur
          (backdrop-blur-xl) contributed almost nothing visually while costing
          a full re-rasterization each frame. Reduced to backdrop-blur-md to
          keep the frosted-glass look at a fraction of the per-frame cost. */}
      <nav className="navbar-dark sticky top-0 z-50 bg-[#0a0a0b]/95 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" onClick={() => { onNavigate(); window.scrollTo({ top: 0 }); }}>
              <BusyBeeLogo className="h-8" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Bots */}
              <div
                className="relative"
                onMouseEnter={() => openMenu('bots')}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => setActiveMenu(activeMenu === 'bots' ? null : 'bots')}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${activeMenu === 'bots' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
                >
                  {t('nav.bots')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'bots' ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Product */}
              <div
                className="relative"
                onMouseEnter={() => openMenu('product')}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => setActiveMenu(activeMenu === 'product' ? null : 'product')}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${activeMenu === 'product' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
                >
                  {t('nav.product')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'product' ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Solutions (teams) */}
              <div
                className="relative"
                onMouseEnter={() => openMenu('solutions')}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => setActiveMenu(activeMenu === 'solutions' ? null : 'solutions')}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${activeMenu === 'solutions' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
                >
                  {t('nav.solutions')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'solutions' ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Resources */}
              <div
                className="relative"
                onMouseEnter={() => openMenu('resources')}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => setActiveMenu(activeMenu === 'resources' ? null : 'resources')}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${activeMenu === 'resources' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
                >
                  {t('nav.resources')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'resources' ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <Link to="/pricing" className="px-4 py-2 text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('nav.pricing')}</Link>
              <Link to="/enterprise" className="px-4 py-2 text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('nav.enterprise')}</Link>
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('nav.log_in')}</Link>
              <Link to="/register" className="bg-amber-500 text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors">{t('nav.get_started_free')}</Link>
              <div className="h-6 w-px bg-gray-700 mx-1" aria-hidden="true" />
              <LanguageSwitcher variant="navbar" />
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-gray-300 hover:text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t('nav.toggle_menu')}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Megamenu (full-width panel). Never taller than the screen: it scrolls instead. */}
        {activeMenu && (
          <div
            className="absolute left-0 right-0 top-full hidden lg:block z-50"
            onMouseEnter={() => openMenu(activeMenu)}
            onMouseLeave={scheduleClose}
          >
            <div className="bg-[#0e0e10] border-b border-gray-800 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] max-h-[calc(100vh-5rem)] overflow-y-auto">
              <div className="max-w-7xl mx-auto px-4 py-6">
                {activeMenu === 'bots' && (
                  <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-8">
                      <SectionTitle>{t('nav.section.what_bots_do')}</SectionTitle>
                      <div className="grid grid-cols-2 gap-x-2">
                        {BOT_GUIDES.map((g) => <GuideItem key={g.path} guide={g} onNavigate={onNavigate} />)}
                      </div>
                    </div>
                    <div className="col-span-4 border-l border-gray-800 pl-6">
                      <SectionTitle className="px-0">{t('nav.section.bot_apps')}</SectionTitle>
                      <div className="grid grid-cols-5 gap-1.5 mb-4">
                        {APP_GUIDES.map((g) => (
                          <Link key={g.path} to={g.path} onClick={onNavigate} title={t(`guide_links.${g.path.slice(1)}.short`)}
                            className="flex items-center justify-center p-2 rounded-lg hover:bg-white/5 transition-colors">
                            <span className="w-8 h-8 rounded-md bg-white p-1.5 flex items-center justify-center"><img src={g.logo} alt={t(`guide_links.${g.path.slice(1)}.short`)} className="w-full h-full object-contain" /></span>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/ai-bots"
                        onClick={onNavigate}
                        className="block p-4 rounded-xl bg-gradient-to-br from-amber-900/30 via-amber-800/20 to-amber-900/30 border border-amber-700/30 hover:border-amber-600/50 transition-colors group"
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <Bot className="w-4 h-4 text-amber-400" />
                          <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">{t('nav.bots_spotlight.title')}</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2 leading-relaxed">{t('nav.bots_spotlight.desc')}</p>
                        <span className="text-amber-400 text-xs font-medium group-hover:text-amber-300 inline-flex items-center gap-1">
                          {t('nav.bots_spotlight.cta')}
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </Link>
                    </div>
                  </div>
                )}

                {activeMenu === 'product' && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-10 grid grid-cols-4 gap-4">
                      {navLinks.product.groups.map((group, i) => (
                        <div key={i} className="min-w-0">
                          <SectionTitle>{t(group.titleKey)}</SectionTitle>
                          {group.items.map((item, j) => (
                            <FeatureItem key={j} item={item} namespace="items" onNavigate={onNavigate} />
                          ))}
                        </div>
                      ))}
                    </div>
                    <div className="col-span-2 border-l border-gray-800 pl-4 min-w-0">
                      <SectionTitle>{t('nav.section.integrations')}</SectionTitle>
                      {navLinks.product.integrations.map((item) => (
                        <PlainLink key={item.key} to={item.path} icon={item.icon} onNavigate={onNavigate}>
                          {t(`nav.items.${item.key}.name`)}
                        </PlainLink>
                      ))}
                      <SectionTitle className="mt-4">{t('nav.section.more')}</SectionTitle>
                      {navLinks.product.more.map((item) => (
                        <PlainLink key={item.key} to={item.path} icon={item.icon} onNavigate={onNavigate}>
                          {t(`nav.items.${item.key}.name`)}
                        </PlainLink>
                      ))}
                      <MoreLink to="/integrations" onNavigate={onNavigate}>{t('nav.view_all_integrations')}</MoreLink>
                    </div>
                  </div>
                )}

                {activeMenu === 'solutions' && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-6">
                      <SectionTitle>{t('nav.section.freelancers')}</SectionTitle>
                      <div className="grid grid-cols-2 gap-x-2">
                        {ROLE_GUIDES.map((g) => <GuideItem key={g.path} guide={g} onNavigate={onNavigate} />)}
                      </div>
                      <MoreLink to="/use-cases" onNavigate={onNavigate}>{t('guide_links.use-cases.name')}</MoreLink>
                    </div>
                    <div className="col-span-3 border-l border-gray-800 pl-4 min-w-0">
                      <SectionTitle>{t('nav.section.switch_from')}</SectionTitle>
                      {alternativeItems.map((item) => (
                        <PlainLink key={item.key} to={item.path} icon={item.icon} onNavigate={onNavigate}>
                          {t(`nav.alternative_items.${item.key}.name`)}
                        </PlainLink>
                      ))}
                      <MoreLink to="/alternatives" onNavigate={onNavigate}>{t('nav.see_all_alternatives')}</MoreLink>
                    </div>
                    <div className="col-span-3 border-l border-gray-800 pl-4 min-w-0">
                      <SectionTitle>{t('nav.section.teams_using')}</SectionTitle>
                      {teamItems.map((item) => (
                        <PlainLink key={item.key} to={item.path} icon={item.icon} onNavigate={onNavigate}>
                          {t(`nav.team_items.${item.key}.name`)}
                        </PlainLink>
                      ))}
                      <MoreLink to="/enterprise" onNavigate={onNavigate}>{t('nav.section.enterprise')}</MoreLink>
                    </div>
                  </div>
                )}

                {activeMenu === 'resources' && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-4 min-w-0">
                      <SectionTitle>{t('nav.section.learn_explore')}</SectionTitle>
                      {resourceItems.filter((r) => r.group === 'learn').map((item) => (
                        <FeatureItem key={item.key} item={item} namespace="resource_items" onNavigate={onNavigate} />
                      ))}
                    </div>
                    <div className="col-span-4 min-w-0">
                      <SectionTitle>{t('nav.section.updates')}</SectionTitle>
                      {resourceItems.filter((r) => r.group === 'updates').map((item) => (
                        <FeatureItem key={item.key} item={item} namespace="resource_items" onNavigate={onNavigate} />
                      ))}
                      <SectionTitle className="mt-4">{t('footer.columns.company')}</SectionTitle>
                      {resourceItems.filter((r) => r.group === 'company').map((item) => (
                        <PlainLink key={item.key} to={item.path} icon={item.icon} onNavigate={onNavigate}>
                          {t(`nav.resource_items.${item.key}.name`)}
                        </PlainLink>
                      ))}
                      <PlainLink to="/contact" icon="mail" onNavigate={onNavigate}>{t('nav.contact_us')}</PlainLink>
                    </div>
                    <div className="col-span-4 border-l border-gray-800 pl-6">
                      <SectionTitle className="px-0">{t('nav.section.free_tools')}</SectionTitle>
                      <Link to="/tools/focus-timer" onClick={onNavigate} className="block p-4 rounded-xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors group">
                        <Timer className="w-5 h-5 text-amber-400 mb-2" />
                        <div className="text-white font-semibold text-sm group-hover:text-amber-400 transition-colors">{t('nav.free_tool.focus_timer_title')}</div>
                        <div className="text-gray-500 text-xs mt-1 leading-snug">{t('nav.free_tool.focus_timer_desc')}</div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 top-16 bg-[#0a0a0b] overflow-y-auto">
          <div className="px-4 py-6 space-y-2">
            {/* Bots accordion */}
            <button
              onClick={() => setMobileSection(mobileSection === 'bots' ? null : 'bots')}
              className="w-full flex items-center justify-between text-white text-base font-semibold py-3 border-b border-gray-800"
            >
              <span>{t('nav.bots')}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${mobileSection === 'bots' ? 'rotate-180' : ''}`} />
            </button>
            {mobileSection === 'bots' && (
              <div className="pl-2 py-3 space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.what_bots_do')}</div>
                  <GuideItem guide={{ path: '/ai-bots', icon: 'bot' }} onNavigate={onNavigate} />
                  {BOT_GUIDES.map((g) => <GuideItem key={g.path} guide={g} onNavigate={onNavigate} />)}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.bot_apps')}</div>
                  {APP_GUIDES.map((g) => <GuideItem key={g.path} guide={g} onNavigate={onNavigate} />)}
                </div>
              </div>
            )}

            {/* Product accordion */}
            <button
              onClick={() => setMobileSection(mobileSection === 'product' ? null : 'product')}
              className="w-full flex items-center justify-between text-white text-base font-semibold py-3 border-b border-gray-800"
            >
              <span>{t('nav.product')}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${mobileSection === 'product' ? 'rotate-180' : ''}`} />
            </button>
            {mobileSection === 'product' && (
              <div className="pl-2 py-3 space-y-5">
                {navLinks.product.groups.map((group, i) => (
                  <div key={i}>
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t(group.titleKey)}</div>
                    {group.items.map((item, j) => (
                      <FeatureItem key={j} item={item} namespace="items" onNavigate={onNavigate} />
                    ))}
                  </div>
                ))}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.integrations')}</div>
                  {navLinks.product.integrations.map((item, i) => (
                    <FeatureItem key={i} item={{ ...item, color: 'yellow' }} namespace="items" onNavigate={onNavigate} />
                  ))}
                  {navLinks.product.more.map((item, i) => (
                    <FeatureItem key={`more-${i}`} item={{ ...item, color: 'yellow' }} namespace="items" onNavigate={onNavigate} />
                  ))}
                </div>
              </div>
            )}

            {/* Solutions accordion */}
            <button
              onClick={() => setMobileSection(mobileSection === 'solutions' ? null : 'solutions')}
              className="w-full flex items-center justify-between text-white text-base font-semibold py-3 border-b border-gray-800"
            >
              <span>{t('nav.solutions')}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${mobileSection === 'solutions' ? 'rotate-180' : ''}`} />
            </button>
            {mobileSection === 'solutions' && (
              <div className="pl-2 py-3 space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.freelancers')}</div>
                  {ROLE_GUIDES.map((g) => <GuideItem key={g.path} guide={g} onNavigate={onNavigate} />)}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.teams_using')}</div>
                  {teamItems.map((item, i) => (
                    <FeatureItem key={i} item={item} namespace="team_items" onNavigate={onNavigate} />
                  ))}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 px-2">{t('nav.section.switch_from', { defaultValue: 'Switch from' })}</div>
                  {alternativeItems.map((item, i) => (
                    <FeatureItem key={i} item={item} namespace="alternative_items" onNavigate={onNavigate} />
                  ))}
                </div>
              </div>
            )}

            {/* Resources accordion */}
            <button
              onClick={() => setMobileSection(mobileSection === 'resources' ? null : 'resources')}
              className="w-full flex items-center justify-between text-white text-base font-semibold py-3 border-b border-gray-800"
            >
              <span>{t('nav.resources')}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${mobileSection === 'resources' ? 'rotate-180' : ''}`} />
            </button>
            {mobileSection === 'resources' && (
              <div className="pl-2 py-3 space-y-1">
                {resourceItems.map((item, i) => (
                  <FeatureItem key={i} item={item} namespace="resource_items" onNavigate={onNavigate} />
                ))}
              </div>
            )}

            <Link to="/pricing" onClick={onNavigate} className="block text-white text-base font-semibold py-3 border-b border-gray-800">{t('nav.pricing')}</Link>
            <Link to="/enterprise" onClick={onNavigate} className="block text-white text-base font-semibold py-3 border-b border-gray-800">{t('nav.enterprise')}</Link>

            <div className="pt-6 space-y-3">
              <div className="flex justify-center">
                <LanguageSwitcher variant="navbar" />
              </div>
              <Link to="/login" onClick={onNavigate} className="block text-center text-gray-300 hover:text-white py-3 border border-gray-700 rounded-lg">{t('nav.log_in')}</Link>
              <Link to="/register" onClick={onNavigate} className="block text-center bg-amber-500 text-black px-4 py-3 rounded-lg font-semibold">{t('nav.get_started_free')}</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
